import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { analytics } from "./firebase/firebase";
import Menu from "./pages/Landing/Menu";
import Docs from "./pages/Landing/Docs/Docs";
import Login from "./pages/Landing/Login";
import Signup from "./pages/Landing/Signup";
import EmailVerification from "./pages/Landing/EmailVerification";
import ProtectedRoute from "./firebase/ProtectedRoute";
import DashboardPage from "./pages/Menu/Dashboard/DashboardPage";
import TrendReportsFeedPage from "./pages/Menu/TrendReports/TrendReportsFeedPage";
import TrendReportDetailPage from "./pages/Menu/TrendReports/TrendReportDetailPage";
import UnsubscribePage from "./pages/Menu/TrendReports/public/UnsubscribePage";
import ResubscribePage from "./pages/Menu/TrendReports/public/ResubscribePage";
import AiChat from "./pages/Menu/AiChat/AiChatPage";
import History from "./pages/Menu/History/History";
import ProductPage from "./pages/Menu/Product/ProductPage";
import ProductProperties from "./pages/Menu/Product/tabs/upload/addProduct/ProductProperties";
import ProductCompetitor from "./pages/Menu/ProductCompetitor/ProductCompetitorPage";
import CompetitorProductProperties from "./pages/Menu/ProductCompetitor/tabs/upload/addProductCompetitor/CompetitorProductProperties";
import ForgotPassword from "./pages/Landing/ForgotPassword";
import AuthAction from "./pages/Landing/AuthAction";
import EditProfile from "./pages/Menu/Profile/EditProfile";
import UserFeedback from "./pages/Menu/UserFeedback/UserFeedback";
import Admin from "./pages/Admin/Admin";
import Feedback from "./pages/Menu/Feedback/Feedback";
import NotFound from "./components/404";
import OnWork from "./components/OnWork";
// ── Bot Tenant SaaS (webchatbot branch) ─────────────────────────────────
import BotDashboard from "./pages/Menu/BotPlatform/BotPage";
import ApiPage from "./pages/Menu/Api/ApiPage";
import FaqDashboard from "./pages/Menu/Faq/FaqDashboard";
import FaqChatbot from "./pages/Menu/Faq/FaqChatbot";
import TgProductAssistant from "./pages/Menu/TgPimsAssistant/TgProductAssistant";
import { AuthProvider } from "./firebase/AuthContext";

const originalError = console.error;
console.error = (...args) => {
  if (/ResizeObserver loop/.test(args[0])) {
    return; // Ignore this specific error
  }
  originalError.call(console, ...args);
};

function App() {
  useEffect(() => {
    // Initialize analytics - this will automatically start collecting basic page views
    if (analytics) {
      console.log("Firebase Analytics initialized successfully");
    }
  }, []);

  // Suppress the specific ResizeObserver loop error
  const resizeError =
    "ResizeObserver loop completed with undelivered notifications";

  window.addEventListener("error", (e) => {
    if (e.message && e.message === resizeError) {
      e.stopImmediatePropagation();
    }
  });

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Default route goes to menu/landing page */}
          <Route path="/" element={<Menu />} />
          <Route path="/menu" element={<Menu />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* <Route path="/OnWork" element={<OnWork />} /> */}
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* Custom action URL for Firebase's verify-email / reset-password links */}
          <Route path="/auth/action" element={<AuthAction />} />
          {/* Public: reached from the trend-report newsletter email, no account needed */}
          <Route path="/newsletter/unsubscribe/:token" element={<UnsubscribePage />} />
          <Route path="/newsletter/resubscribe/:token" element={<ResubscribePage />} />
          <Route
            path="/edit-profile"
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trend-reports"
            element={
              <ProtectedRoute requiredRole={"trend.read"}>
                <TrendReportsFeedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trend-reports/:date"
            element={
              <ProtectedRoute requiredRole={"trend.read"}>
                <TrendReportDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-chat"
            element={
              <ProtectedRoute>
                <AiChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute requiredRole={"assistant.history"}>
                <History />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product"
            element={
              <ProtectedRoute requiredRole={"product.read"}>
                <ProductPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/update"
            element={
              <ProtectedRoute requiredRole={"product.edit"}>
                <OnWork />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product-properties"
            element={
              <ProtectedRoute requiredRole={"product.read"}>
                <ProductProperties />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product-competitor"
            element={
              <ProtectedRoute requiredRole={"competitor.read"}>
                <ProductCompetitor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/competitor-product-properties"
            element={
              <ProtectedRoute requiredRole={"competitor.read"}>
                <CompetitorProductProperties />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-feedback"
            element={
              <ProtectedRoute requiredRole={"feedback.read"}>
                <UserFeedback />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <ProtectedRoute requiredRole={"assistant.feedback"}>
                <Feedback />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole={"admin_panel.access"}>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/api"
            element={
              <ProtectedRoute requiredRole={["api.view_own", "api.view_all"]}>
                <ApiPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/bot-platform/dashboard"
            element={
              <ProtectedRoute
                requiredRole={["webchatbot.view_own", "webchatbot.view_all"]}
              >
                <BotDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/faq"
            element={
              <ProtectedRoute requiredRole={"knowledge.faq.review"}>
                <FaqDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="faq/ai-chat"
            element={
              <ProtectedRoute>
                <FaqChatbot />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tg-product-assistant"
            element={
              <ProtectedRoute>
                <TgProductAssistant />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
